import { Timestamp } from '@klear/time'
import type { GeneratedDocumentAccountIds } from '@klear/trader-legal-agreement/generated-legal-agreement-document.wire'

export type GeneratedLegalAgreementDocumentIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type GeneratedLegalAgreementDocumentJson = {
  identity: GeneratedLegalAgreementDocumentIdentity
  legalAgreementId: number
  traderId: number
  userId: number
  documentHash: string
  documentContentUrl: string
  documentSignatureUrl?: string
  acceptedAt?: number | Timestamp
  traderLegalAgreementAcceptanceId?: number
  accountIds?: GeneratedDocumentAccountIds
  unsignedPdfHash?: string
  unsignedPdfStorageKey?: string
  signedPdfHash?: string
  legalAgreementVersion?: number
  signingSessionId?: number
  createdAt?: number | Timestamp
}

export function generatedLegalAgreementDocumentToJson(document: GeneratedLegalAgreementDocument) {
  return {
    identity: document.identity,
    legalAgreementId: document.legalAgreementId,
    traderId: document.traderId,
    userId: document.userId,
    documentHash: document.documentHash,
    documentContentUrl: document.documentContentUrl,
    documentSignatureUrl: document.documentSignatureUrl,
    acceptedAt: document.acceptedAt?.epochMs,
    traderLegalAgreementAcceptanceId: document.traderLegalAgreementAcceptanceId,
    accountIds: document.accountIds,
    unsignedPdfHash: document.unsignedPdfHash,
    unsignedPdfStorageKey: document.unsignedPdfStorageKey,
    signedPdfHash: document.signedPdfHash,
    legalAgreementVersion: document.legalAgreementVersion,
    signingSessionId: document.signingSessionId,
    createdAt: document.createdAt?.epochMs
  }
}

export class GeneratedLegalAgreementDocument {
  static readonly __klearClass = 'GeneratedLegalAgreementDocument'

  readonly identity: GeneratedLegalAgreementDocumentIdentity
  legalAgreementId: number
  traderId: number
  userId: number
  documentHash: string
  documentContentUrl: string
  documentSignatureUrl?: string
  acceptedAt?: Timestamp
  traderLegalAgreementAcceptanceId?: number
  accountIds?: GeneratedDocumentAccountIds
  unsignedPdfHash?: string
  unsignedPdfStorageKey?: string
  signedPdfHash?: string
  legalAgreementVersion?: number
  signingSessionId?: number
  createdAt?: Timestamp

  constructor(props: GeneratedLegalAgreementDocumentJson) {
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.legalAgreementId = props.legalAgreementId
    this.traderId = props.traderId
    this.userId = props.userId
    this.documentHash = props.documentHash
    this.documentContentUrl = props.documentContentUrl
    this.documentSignatureUrl = props.documentSignatureUrl
    this.acceptedAt = props.acceptedAt != null ? new Timestamp(props.acceptedAt) : undefined
    this.traderLegalAgreementAcceptanceId = props.traderLegalAgreementAcceptanceId
    this.accountIds = props.accountIds
    this.unsignedPdfHash = props.unsignedPdfHash
    this.unsignedPdfStorageKey = props.unsignedPdfStorageKey
    this.signedPdfHash = props.signedPdfHash
    this.legalAgreementVersion = props.legalAgreementVersion
    this.signingSessionId = props.signingSessionId
    this.createdAt = props.createdAt != null ? new Timestamp(props.createdAt) : undefined
    this.assertValid()
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  assertValid(): void {
    if (!this.legalAgreementId) {
      throw new Error('GeneratedLegalAgreementDocument legalAgreementId is required')
    }
    if (!this.traderId) {
      throw new Error('GeneratedLegalAgreementDocument traderId is required')
    }
  }

  toJSON() {
    return generatedLegalAgreementDocumentToJson(this)
  }
}
